import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiUsers, FiTool, FiDollarSign, FiChevronRight, FiRefreshCw, FiSliders, FiPercent, FiFilePlus, FiFlag, FiCreditCard, FiAlertTriangle } from 'react-icons/fi';
import { buscarParametrosNf, emitirNota, baixarDanfse, previaDanfse, buscarAliquotaSimples, registrarAliquotaSimples } from '../../../services/nf';
import { calcularRetencoes, calcularVencimento, montarCorpo, valoresRetencao, valorTotalServico } from './cobranca';
import ModalAliquotaMes from './ModalAliquotaMes';
import { listarPerfisNf } from '../../../services/nfPerfis';
import { listarMunicipios, municipioPorCodigo } from '../../../services/municipios';
import EtapaPessoas, { pendenciasDoEmitente } from './EtapaPessoas';
import EtapaServico from './EtapaServico';
import EtapaValores from './EtapaValores';
import ModalRevisao from './ModalRevisao';
import { NfConfigModal } from '../../clientes';
import ModalOpcoesEspeciais, { opcoesAlteradas } from './ModalOpcoesEspeciais';
import FaixaSecao from './FaixaSecao';
import ModalExtras, { EXTRAS, extrasAlterados } from './ModalExtras';
import { formatarDocumento } from '../../../utils/documento';
import styles from './Emitir.module.css';
import { exigeEvento } from './EventoServico';
import { ehPortal } from '../../../utils/portal';

/* Assistente de emissão de NFS-e em 4 etapas, no mesmo encadeamento do
   Portal Nacional (Pessoas → Serviço → Valores → Emitir), pra quem já
   conhece o portal não precisar reaprender.

   O estado da nota inteira vive aqui, não em cada etapa: a revisão final
   precisa enxergar tudo, e trocar de etapa não pode perder o que já foi
   digitado. */

/* `tela` agrupa etapas que cabem juntas: Pessoas e Serviço ficaram curtas
   depois que os campos raros saíram, então dividem a mesma tela e a trilha
   só rola até a faixa correspondente (`ancora`). */

function hoje() {
  /* data local, não UTC: perto da meia-noite toISOString() devolve o dia
     seguinte e a competência sairia errada */
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function estadoInicial(prestador) {
  return {
    informarIbsCbs: !!prestador?.nfInformarIbsCbs,
    competencia: hoje(),
    numeroDps: '',
    compraGovernamental: false,

    tomadorDocumento: '', tomadorNome: '', tomadorEmail: '', tomadorTelefone: '',
    tomadorCep: '', tomadorCodigoMunicipio: '', tomadorLogradouro: '',
    tomadorNumero: '', tomadorComplemento: '', tomadorBairro: '',
    destinatarioAdquirente: '', intermediario: '',

    codigoMunicipioPrestacao: prestador?.nfCodigoMunicipioPrestacao || prestador?.nfCodigoMunicipioEmissor || '',
    codigoMunicipioIncidencia: '',
    codigoTributacaoNacional: prestador?.nfCodigoTributacaoNacional || '',
    codigoTributacaoMunicipal: prestador?.nfCodigoTributacaoMunicipal || '',
    cTribMunExige: false,
    itemNbs: '', codigoOperacao: '',
    descricaoServico: prestador?.nfDescricaoServico || '',
    tributacaoIssqn: 1,
    docResponsabilidadeTecnica: '', documentoReferencia: '',
    informacoesComplementares: '', numeroPedido: '',

    valorServico: prestador?.nfValorPadrao || '',
    descontoIncondicionado: '', descontoCondicionado: '',
    reterIss: !!prestador?.nfPossuiRetencaoPadrao,
    aliquotaIss: prestador?.nfAliquotaIss ?? '',
    regimeEspecial: prestador?.nfRegimeEspecial ?? 0,
    situacaoPisCofins: '', irrf: '', contribuicoesRetidas: '', contribuicaoPrevidenciaria: '',
    cstIbsCbs: '', classificacaoIbsCbs: '',
    valorAproximadoTributos: '',
    /* do perfil: retenções federais (%), cobrança e texto fixo do corpo */
    retIrrf: '', retInss: '', retPis: '', retCofins: '', retCsll: '',
    /* dados de pagamento são do emissor (a conta dele); "possui retenção" no
       cadastro liga o corpo no formato de cálculo (base, retenções, líquido) */
    dadosPagamento: prestador?.nfDadosPagamento || '', vencimento: '', textoComplementar: '',
    formatoRetencao: !!prestador?.nfPossuiRetencaoPadrao,
    valorUnitario: '', quantidade: '', usaQuantidade: false,
    /* Atividade/Evento: só pro item 12 da lista (EventoServico.jsx) */
    eventoNome: '', eventoInicio: '', eventoFim: '', eventoCep: '', eventoLogradouro: '', eventoNumero: '', eventoComplemento: '', eventoBairro: '',
    adicionais: [],
  };
}

function AssistenteEmissao({ prestador, onTrocarPrestador }) {
  const [revisaoAberta, setRevisaoAberta] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  /* prévia do PDF na revisão: o documento como vai sair, marcado "apenas conferência" */
  const [previaUrl, setPreviaUrl] = useState(null);
  const [erroPrevia, setErroPrevia] = useState('');
  /* '' enquanto busca, mensagem quando falha -- o PDF vem da ADN, que cai
     de vez em quando, e deixar "carregando" pra sempre faz parecer que a
     nota não saiu (ela saiu: a autorização é da transmissão, não do PDF) */
  const [erroPdf, setErroPdf] = useState('');
  const [dados, setDados] = useState(() => estadoInicial(prestador));
  const [perfis, setPerfis] = useState([]);
  const [perfilId, setPerfilId] = useState('');
  const [certificadoConfigurado, setCertificadoConfigurado] = useState(false);
  const [municipios, setMunicipios] = useState([]);
  const [emitindo, setEmitindo] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  /* corrigir o cadastro sem sair da emissão -- fechando o modal, o que já
     foi preenchido na nota continua lá */
  const [corrigindoCadastro, setCorrigindoCadastro] = useState(false);
  const [opcoesAbertas, setOpcoesAbertas] = useState(false);
  const [extraAberto, setExtraAberto] = useState(null);
  const [prestadorAtual, setPrestadorAtual] = useState(prestador);

  const set = useCallback((campo, valor) => setDados(d => ({ ...d, [campo]: valor })), []);

  /* aplica um perfil por cima do que já está preenchido. Campo vazio no
     perfil não apaga o que o usuário digitou -- perfil completa, não
     atropela. */
  const aplicarPerfil = useCallback((p) => {
    if (!p) return;
    setDados(d => ({
      ...d,
      codigoTributacaoNacional: p.codigo_tributacao_nacional || d.codigoTributacaoNacional,
      codigoTributacaoMunicipal: p.codigo_tributacao_municipal || (p.codigo_tributacao_nacional && p.codigo_tributacao_nacional !== d.codigoTributacaoNacional ? '' : d.codigoTributacaoMunicipal),
      itemNbs: p.item_nbs || d.itemNbs,
      codigoOperacao: p.codigo_operacao || d.codigoOperacao,
      descricaoServico: p.descricao_servico || d.descricaoServico,
      codigoMunicipioPrestacao: p.codigo_municipio_prestacao || d.codigoMunicipioPrestacao,
      valorServico: p.valor_padrao ?? d.valorServico,
      quantidade: p.valor_padrao != null ? '' : d.quantidade,
      /* Simples: a alíquota confirmada do mês vale mais que a do perfil */
      aliquotaIss: d.aliquotaMes ?? p.aliquota_iss ?? d.aliquotaIss,
      tributacaoIssqn: p.tributacao_issqn ?? d.tributacaoIssqn,
      reterIss: p.reter_iss ?? d.reterIss,
      retIrrf: p.ret_irrf ?? '', retInss: p.ret_inss ?? '', retPis: p.ret_pis ?? '', retCofins: p.ret_cofins ?? '', retCsll: p.ret_csll ?? '',
      dadosPagamento: d.dadosPagamento || p.dados_pagamento || '',
      textoComplementar: p.texto_complementar || '',
      vencimento: calcularVencimento(p.vencimento_tipo, p.vencimento_valor, hoje()) || d.vencimento,
    }));
  }, []);

  /* depois de corrigir o perfil vindo da revisão: só o que é do perfil
     (códigos, retenções, texto fixo) -- valor, descrição e vencimento já
     podem ter vindo do tomador ou da digitação e não se perdem */
  const reaplicarCodigos = useCallback((p) => {
    setDados(d => ({
      ...d,
      codigoTributacaoNacional: p.codigo_tributacao_nacional || d.codigoTributacaoNacional,
      codigoTributacaoMunicipal: p.codigo_tributacao_municipal || d.codigoTributacaoMunicipal,
      itemNbs: p.item_nbs || d.itemNbs,
      codigoOperacao: p.codigo_operacao || d.codigoOperacao,
      codigoMunicipioPrestacao: p.codigo_municipio_prestacao || d.codigoMunicipioPrestacao,
      aliquotaIss: d.aliquotaMes ?? p.aliquota_iss ?? d.aliquotaIss,
      tributacaoIssqn: p.tributacao_issqn ?? d.tributacaoIssqn,
      reterIss: p.reter_iss ?? d.reterIss,
      retIrrf: p.ret_irrf ?? '', retInss: p.ret_inss ?? '', retPis: p.ret_pis ?? '', retCofins: p.ret_cofins ?? '', retCsll: p.ret_csll ?? '',
      textoComplementar: p.texto_complementar || d.textoComplementar,
    }));
  }, []);

  useEffect(() => { listarMunicipios().then(setMunicipios).catch(() => {}); }, []);

  const recarregarParametros = useCallback(() => {
    if (!prestador?.id) return;
    buscarParametrosNf(prestador.id)
      .then(p => {
        setCertificadoConfigurado(!!p.certificadoConfigurado);
        if (p.proximoNumeroDps) set('numeroDps', String(p.proximoNumeroDps));
      })
      .catch(e => setErro(e.message));
  }, [prestador, set]);

  useEffect(() => {
    if (!prestador?.id) return;
    setDados(estadoInicial(prestador));
    setResultado(null);
    setRevisaoAberta(false);
    setPdfUrl(null);

    buscarParametrosNf(prestador.id)
      .then(p => {
        setCertificadoConfigurado(!!p.certificadoConfigurado);
        if (p.proximoNumeroDps) set('numeroDps', String(p.proximoNumeroDps));
      })
      .catch(e => setErro(e.message));

    if (Number(prestador?.nfRegimeTributario) === 3) {
      buscarAliquotaSimples(prestador.id).then(a => { if (a.doMes) setDados(d => ({ ...d, aliquotaIss: a.doMes.aliquota, aliquotaMes: a.doMes.aliquota })); }).catch(() => {});
    }

    listarPerfisNf(prestador.id)
      .then(r => {
        const lista = r.items || [];
        setPerfis(lista);
        /* com um perfil só, aplica sem perguntar -- foi o pedido explícito;
           com vários, pré-seleciona o marcado como padrão */
        const escolhido = lista.length === 1 ? lista[0] : lista.find(p => p.padrao);
        if (escolhido) { setPerfilId(escolhido.id); aplicarPerfil(escolhido); }
      })
      .catch(() => {});
  }, [prestador, set, aplicarPerfil]);

  /* botão "Corrigir"/"Ir ao campo" da revisão */
  function corrigirPendencia(acao) {
    setRevisaoAberta(false);
    /* cliente não mexe em parâmetro: é o escritório que ajusta */
    if (ehPortal() && !acao?.ancora) {
      setErro('Esse dado é configurado pelo escritório. Fale com a Essensials para ajustar e depois emita de novo.');
      return;
    }
    if (acao?.ancora) {
      setTimeout(() => document.getElementById(acao.ancora)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
      return;
    }
    setCorrigindoCadastro({ bloco: acao?.bloco, perfil: acao?.perfil ? (perfilId || 'novo') : null, voltarRevisao: true });
  }

  async function fecharCorrecao() {
    const c = corrigindoCadastro;
    setCorrigindoCadastro(false);
    recarregarParametros();
    try {
      const lista = (await listarPerfisNf(prestador.id)).items || [];
      setPerfis(lista);
      const atual = lista.find(p => p.id === perfilId);
      if (atual) reaplicarCodigos(atual);
      else {
        const escolhido = lista.length === 1 ? lista[0] : lista.find(p => p.padrao);
        if (escolhido) { setPerfilId(escolhido.id); aplicarPerfil(escolhido); }
      }
    } catch { /* sem perfis novos: fica o que estava */ }
    if (c?.voltarRevisao) abrirRevisao();
  }

  function trocarPerfil(id) {
    setPerfilId(id);
    aplicarPerfil(perfis.find(p => p.id === id));
  }

  const pendencias = useMemo(() => {
    /* prestadorAtual: corrigido pelo modal de parâmetros, a pendência some na hora */
    const p = pendenciasDoEmitente(prestadorAtual);
    if (!certificadoConfigurado) p.push('certificado digital');
    return p;
  }, [prestadorAtual, certificadoConfigurado]);

  const alteracoesEspeciais = useMemo(
    () => [...opcoesAlteradas(dados), ...extrasAlterados(dados)], [dados]);

  /* retenções pelo perfil (% do serviço); valor digitado à mão nas opções de
     tributação federal vence o %. "Tributos aprox." é só informativo. */
  const retencoes = useMemo(() => calcularRetencoes(dados), [dados]);
  const tributosAprox = useMemo(() => {
    const valor = Number(dados.valorServico || 0);
    /* ME/EPP do Simples: vai na nota como pTotTribSN, com o percentual do
       cadastro (não a alíquota do mês, que é só a do ISS) */
    if (Number(prestadorAtual?.nfRegimeTributario) === 3) {
      const aliq = Number(prestadorAtual?.nfPercentualTributos || 0);
      return valor > 0 && aliq > 0
        ? [{ chave: 'aprox', rotulo: `Tributos aprox. (Simples ${String(aliq).replace('.', ',')}%)`, valor: valor * (aliq / 100), origem: 'percentual do cadastro' }] : [];
    }
    const pctTrib = Number(prestadorAtual?.nfPercentualTributos || 0);
    return valor > 0 && pctTrib > 0 && !dados.valorAproximadoTributos
      ? [{ chave: 'aprox', rotulo: 'Tributos aprox.', valor: valor * (pctTrib / 100), origem: pctTrib + '% do cadastro' }] : [];
  }, [dados.valorServico, dados.valorAproximadoTributos, prestadorAtual]);
  const corpo = useMemo(() => montarCorpo(dados, retencoes), [dados, retencoes]);
  const [pedindoAliquota, setPedindoAliquota] = useState(null); // { mes, ultima }

  const municipioPrestacaoNome = useMemo(() => {
    const m = municipioPorCodigo(municipios, dados.codigoMunicipioPrestacao);
    return m ? `${m.nome}/${m.uf}` : (dados.codigoMunicipioPrestacao || '—');
  }, [municipios, dados.codigoMunicipioPrestacao]);

  /* monta o pedido no formato que a rota /nf/emitir espera (ver
     services/nfse/dps.js) */
  function montarPedido(aliquotaDoMes) {
    /* com a alíquota do mês recém-confirmada, recalcula retenções e corpo com ela
       (o estado da tela ainda tem a anterior neste momento) */
    const dadosNota = aliquotaDoMes != null ? { ...dados, aliquotaIss: aliquotaDoMes } : dados;
    const retencoesNota = calcularRetencoes(dadosNota);
    const corpoNota = montarCorpo(dadosNota, retencoesNota);
    const doc = String(dados.tomadorDocumento).replace(/\D/g, '');
    const pedido = {
      numero_dps: dados.numeroDps,
      competencia: dados.competencia,
      tomador: {
        documento: doc,
        nome: dados.tomadorNome.trim(),
        email: dados.tomadorEmail || undefined,
      },
      servico: {
        codigo_tributacao_nacional: dados.codigoTributacaoNacional,
        codigo_tributacao_municipal: dados.codigoTributacaoMunicipal || undefined,
        codigo_municipio_prestacao: dados.codigoMunicipioPrestacao,
        descricao: dados.descricaoServico.trim(),
        /* o código NBS estava sendo lido do perfil e nunca enviado: a nota
           saía sem ele, e nota sem NBS é risco de autuação pro cliente */
        codigo_nbs: dados.itemNbs,
      },
      /* nomes conferidos contra services/nfse/dps.js -- errar aqui não dá
         erro, gera nota ERRADA em silêncio (o campo simplesmente não entra
         no XML). tipo_retencao_iss: 1=não retido, 2=retido pelo tomador. */
      valores: {
        valor_servico: valorTotalServico(dadosNota),
        desconto_incondicionado: dados.descontoIncondicionado || 0,
        trib_issqn: dados.tributacaoIssqn,
        aliquota_iss: aliquotaDoMes != null ? aliquotaDoMes : (dados.aliquotaIss === '' ? null : dados.aliquotaIss),
        tipo_retencao_iss: dados.reterIss ? 2 : 1,
        ...valoresRetencao(dadosNota, retencoesNota, prestadorAtual?.nfSituacaoPisCofins ? String(prestadorAtual.nfSituacaoPisCofins).padStart(2, '0') : ''),
      },
    };
    if (dados.tomadorCodigoMunicipio || dados.tomadorCep) {
      pedido.tomador.endereco = {
        codigo_municipio: dados.tomadorCodigoMunicipio,
        cep: dados.tomadorCep,
        logradouro: dados.tomadorLogradouro,
        numero: dados.tomadorNumero,
        complemento: dados.tomadorComplemento,
        bairro: dados.tomadorBairro,
      };
    }
    /* corpo da nota: retenções, vencimento, pagamento, texto do perfil e o
       que foi escrito em "informações complementares" */
    if (corpoNota) pedido.informacoes_complementares = corpoNota;
    if (exigeEvento(dados.codigoTributacaoNacional)) {
      pedido.atividade_evento = {
        nome: dados.eventoNome, data_inicio: dados.eventoInicio, data_fim: dados.eventoFim,
        cep: dados.eventoCep, logradouro: dados.eventoLogradouro, numero: dados.eventoNumero,
        complemento: dados.eventoComplemento, bairro: dados.eventoBairro,
      };
    }
    if (dados.docResponsabilidadeTecnica) pedido.doc_responsabilidade_tecnica = dados.docResponsabilidadeTecnica;
    if (dados.documentoReferencia) pedido.doc_referencia = dados.documentoReferencia;
    return pedido;
  }

  useEffect(() => {
    if (!revisaoAberta || resultado?.autorizada || !prestador?.id) return undefined;
    let vivo = true;
    let url = null;
    setPreviaUrl(null); setErroPrevia('');
    previaDanfse(prestador.id, montarPedido())
      .then(blob => { if (!vivo) return; url = URL.createObjectURL(blob); setPreviaUrl(url); })
      .catch(e => vivo && setErroPrevia(e.message));
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
    // a prévia é tirada no momento em que a revisão abre
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisaoAberta]);

  /* a revisão sempre abre sobre a nota ATUAL: o resultado da anterior
     (autorizada ou recusada) não pode reaparecer -- foi o que aconteceu ao
     emitir uma segunda nota sem sair da tela */
  function abrirRevisao() {
    setResultado(null); setPdfUrl(null); setErroPdf('');
    setRevisaoAberta(true);
  }

  function fecharRevisao() {
    setRevisaoAberta(false);
    if (resultado?.autorizada) {
      /* nota emitida: limpa o resultado e pega o próximo número de DPS */
      setResultado(null); setPdfUrl(null); setErroPdf('');
      recarregarParametros();
    }
  }

  function baixarXml() {
    if (!resultado?.nfseXml) return;
    const blob = new Blob([resultado.nfseXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NFSe-${resultado.chaveAcesso || 'nota'}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /* busca o DANFSe pra mostrar na hora. Se falhar, a nota JÁ foi
     autorizada -- o erro do PDF não pode virar erro de emissão, senão
     pareceria que a nota não saiu. Por isso a falha aparece à parte, com
     a opção de tentar de novo. */
  async function buscarPdf(chaveAcesso) {
    setErroPdf(''); setPdfUrl(null);
    try {
      const blob = await baixarDanfse(prestador.id, chaveAcesso);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      setErroPdf(e.message || 'Não foi possível baixar o PDF agora.');
    }
  }

  /* Simples (ME/EPP): a 1ª nota do mês só sai depois de confirmar a alíquota
     do mês (nova ou manter a anterior) -- fica o histórico */
  async function emitir(aliquotaDoMes) {
    setErro('');
    if (aliquotaDoMes == null && Number(prestadorAtual?.nfRegimeTributario) === 3) {
      try {
        const a = await buscarAliquotaSimples(prestador.id);
        if (!a.doMes) { setPedindoAliquota({ mes: a.mes, ultima: a.ultima }); return; }
        aliquotaDoMes = a.doMes.aliquota;
      } catch (e) { setErro(e.message); return; }
    }
    setEmitindo(true); setResultado(null); setErroPdf(''); setPdfUrl(null);
    try {
      const r = await emitirNota(prestador.id, montarPedido(aliquotaDoMes));
      setResultado({
        autorizada: !!r.autorizada,
        chaveAcesso: r.chaveAcesso,
        nfseXml: r.nfseXml || null,
        mensagem: r.saida || '', // a rota /nf/emitir devolve o log em "saida"
      });
      if (r.autorizada && r.chaveAcesso) buscarPdf(r.chaveAcesso);
    } catch (e) {
      setResultado({ autorizada: false, mensagem: e.message });
    } finally {
      setEmitindo(false);
    }
  }

  return (
    <div>
      {/* Cabeçalho: quem emite + troca de cliente. Fica aqui em cima porque
          é a informação que contextualiza a tela inteira -- no rodapé
          ficava longe demais de quem quer conferir antes de digitar. */}
      <div className={styles.cabecalhoEmitente}>
        <div style={{ minWidth: 0 }}>
          <span className={styles.cabecalhoRotulo}>Emitindo em nome de</span>
          <strong className={styles.cabecalhoNome}>
            {prestadorAtual.empresa || prestadorAtual.nome}
          </strong>
          <span className={styles.cabecalhoDoc}>{formatarDocumento(prestadorAtual.cnpj || prestadorAtual.cpf)}</span>
        </div>
        <div className={styles.cabecalhoAcoes}>
          {/* campos raros do layout vivem atrás deste ícone. O ponto laranja
              aparece quando algum sai do padrão -- esconder sem avisar seria
              trocar poluição visual por risco de emitir errado. */}
          {[
            { tipo: 'descontos',      icone: FiPercent,    titulo: 'Descontos' },
            { tipo: 'complementares', icone: FiFilePlus,   titulo: 'Informações complementares' },
            { tipo: 'federal',        icone: FiFlag,       titulo: 'Tributação federal' },
            { tipo: 'tributos',       icone: FiCreditCard, titulo: 'Valor aproximado dos tributos' },
          ].map(({ tipo, icone: Icone, titulo }) => {
            const resumo = EXTRAS[tipo].resumo(dados);
            return (
              <button key={tipo} type="button"
                className={`${styles.btnOpcoes} ${resumo ? styles.btnOpcoesAlterado : ''}`}
                onClick={() => setExtraAberto(tipo)}
                title={resumo ? `${titulo}: ${resumo}` : titulo}>
                <Icone size={14} />
                {resumo && <span className={styles.pontoAlerta} />}
              </button>
            );
          })}
          <button type="button"
            className={`${styles.btnOpcoes} ${opcoesAlteradas(dados).length ? styles.btnOpcoesAlterado : ''}`}
            onClick={() => setOpcoesAbertas(true)}
            title={opcoesAlteradas(dados).length
              ? `Opções especiais: ${opcoesAlteradas(dados).join(' · ')}`
              : 'Opções especiais (compra governamental, destinatário, intermediário)'}>
            <FiSliders size={14} />
            {opcoesAlteradas(dados).length > 0 && <span className={styles.pontoAlerta} />}
          </button>
          {onTrocarPrestador && (
          <button type="button" className={styles.btnTrocarCliente} onClick={onTrocarPrestador}>
            <FiRefreshCw size={12} /> Trocar cliente
          </button>
          )}
        </div>
      </div>

      {erro && (
        <div className={styles.erroCaixa} style={{ marginBottom: 12 }}>
          <FiAlertTriangle size={14} />
          <div>{erro}</div>
        </div>
      )}

      {/* ordem de preenchimento: o tomador primeiro (ele já traz serviço, valor e
          vencimento do cadastro), depois valores e serviço */}
      <>
          <FaixaSecao icone={FiUsers} titulo="Pessoas" ancora="secao-pessoas" />
          <EtapaPessoas prestador={prestadorAtual} dados={dados} set={set}
            certificadoConfigurado={certificadoConfigurado}
            onCorrigirCadastro={ehPortal() ? null : () => setCorrigindoCadastro(true)} />
          <FaixaSecao icone={FiDollarSign} titulo="Valores" ancora="secao-valores" />
          <EtapaValores dados={dados} set={set} competencia={dados.competencia}
            onCompetencia={(v) => set('competencia', v)}
            issFlexivel={!!prestadorAtual?.nfIssFlexivel}
            simplesNacional={[2, 3].includes(Number(prestadorAtual?.nfRegimeTributario))}
            retencoes={[...retencoes, ...tributosAprox]} />
          <FaixaSecao icone={FiTool} titulo="Serviço" ancora="secao-servico" />
          <EtapaServico dados={dados} set={set} perfis={perfis} clienteId={prestador?.id} corpo={corpo}
            perfilId={perfilId} onTrocarPerfil={trocarPerfil}
            municipioPrestacaoNome={dados.codigoMunicipioPrestacao ? municipioPrestacaoNome : ''}
            onAbrirCadastro={ehPortal() ? null : () => setCorrigindoCadastro(true)} />
      </>

      {revisaoAberta && (
        <ModalRevisao
          prestador={prestadorAtual} dados={dados}
          municipioNome={municipioPrestacaoNome} pendencias={pendencias}
          opcoesEspeciais={alteracoesEspeciais} retencoes={[...retencoes, ...tributosAprox]} corpo={corpo} semPerfil={!perfilId}
          emitindo={emitindo} onEmitir={() => emitir()} resultado={resultado}
          pdfUrl={pdfUrl} erroPdf={erroPdf} onBaixarXml={baixarXml}
          onTentarPdf={() => resultado?.chaveAcesso && buscarPdf(resultado.chaveAcesso)}
          previaUrl={previaUrl} erroPrevia={erroPrevia}
          onCorrigir={corrigirPendencia}
          onFechar={fecharRevisao} />
      )}

      {pedindoAliquota && (
        <ModalAliquotaMes mes={pedindoAliquota.mes} ultima={pedindoAliquota.ultima}
          onConfirmar={async (aliquota, mantida) => {
            await registrarAliquotaSimples(prestador.id, { competencia: pedindoAliquota.mes, aliquota, mantida });
            setPedindoAliquota(null);
            setDados(d => ({ ...d, aliquotaIss: aliquota, aliquotaMes: aliquota }));
            emitir(aliquota);
          }} />
      )}

      {extraAberto && (
        <ModalExtras tipo={extraAberto} dados={dados} set={set}
          onFechar={() => setExtraAberto(null)} />
      )}

      {opcoesAbertas && (
        <ModalOpcoesEspeciais dados={dados} set={set} onFechar={() => setOpcoesAbertas(false)} />
      )}

      {corrigindoCadastro && (
        <NfConfigModal
          cliente={prestadorAtual}
          blocoInicial={corrigindoCadastro?.bloco}
          perfilInicial={corrigindoCadastro?.perfil}
          onPerfilSalvo={corrigindoCadastro?.voltarRevisao ? fecharCorrecao : undefined}
          onFechar={fecharCorrecao}
          onAtualizado={(atualizado) => setPrestadorAtual(atualizado)}
        />
      )}

      <div className={styles.navegacao}>
        <span />
        <button type="button" className={styles.btnAvancar} onClick={abrirRevisao}>
          Revisar e emitir <FiChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export default AssistenteEmissao;
